import { useState } from 'react';
import { IconArchive, IconArrowBackUp, IconCalendarEvent, IconChartBar, IconClockHour4, IconContainer, IconCopy, IconDownload, IconEdit, IconFileText, IconHistory, IconLock, IconMapPin, IconPlayerPause, IconPlus, IconReceipt, IconSparkles, IconUpload, IconUser, IconX } from '@tabler/icons-react';
import { DataError, useList } from '@hana/data';
import { useT } from '@hana/i18n';
import { HeaderTool, Page, PageHeader, useLayout } from '@hana/shell';
import { Button, Drawer, Segmented, Skeleton, StateView, useToast, type ScreenState } from '@hana/ui';
import { financeScreenState, useFinanceAccess } from '../finance/access';
import { countOf, dk, dkt, kk, logOf, normRows, normStats, NORM_APR, NORM_PORT, NORM_RCPT, NORM_ST, NORM_SV, NORM_UNIT, resolveNorms, svOf, thrText, vnd, waitOf, type NormView, type NormVerView } from './normLogic';
import type { NormRow } from './normTypes';
import { Code, DocTags, initSel, NatTag, pill, tile } from './normParts';
import { NormsCompact } from './NormsCompact';
import s from './norms.module.css';
import './norms.i18n';

// Mint-Norms (1536): Định mức CHI PHÍ theo dịch vụ × cảng / kho — màn tiền, sau quyền money (?money=view). Chỉ đọc; nút ghi báo toast.
// Mã phí chỉ dùng mã thật trong sysServiceCode; không có mã / danh mục nào cho khoản chi cán bộ.

type Rows = ReturnType<typeof normRows>;

function Group({ rows, sup, title, count, pick, sel }: { rows: Rows; sup: boolean; title: string; count: number; pick: (id: string, v: number | null) => void; sel: { id: string | null; v: number | null } }) {
  const tr = useT();
  const cols = sup ? s.cSup : s.cStd;
  return (
    <div>
      <div className={s.gh}>
        <h3>{title}</h3>
        <span className={s.lb}>{count}</span>
        {sup && (
          <>
            <span className={s.only} title={tr('nrm.privTip')}><IconUser size={13} stroke={1.8} aria-hidden="true" />{tr('nrm.priv')}</span>
            <span className={s.tag} title={tr('nrm.ndc')} style={{ color: 'var(--lb)', background: 'var(--sub)' }}>NDC</span>
          </>
        )}
      </div>
      <div className={`${s.hd} ${cols}`} aria-hidden="true">
        <span>{tr('nrm.c.code')}</span><span>{tr('nrm.c.fee')}</span>{sup && <span>{tr('nrm.c.rcpt')}</span>}<span>{tr('nrm.port')}</span><span>{tr('nrm.unit')}</span>
        <span className={s.r}>{tr('nrm.c.norm')}</span><span className={s.r}>{tr('nrm.thr')}</span><span>{tr('nrm.apr')}</span><span>{tr('nrm.eff')}</span>
      </div>
      {rows.map(({ n, x, old }) => {
        const nx = old ? null : n.next;
        const on = sel.id === n.id && (!old || sel.v === x.i);
        const lab = tr('nrm.view', { code: n.code || tr('nrm.pending'), name: n.name });
        return (
          <button key={`${n.id}-${x.v}`} type="button" className={`${s.row} ${cols}`} style={{ opacity: old ? 0.62 : 1, background: on ? 'var(--pri-bg)' : undefined }} aria-current={on} aria-label={lab} onClick={() => pick(n.id, old ? x.i : null)}>
            <span className={s.flex}><Code n={n} /></span>
            <span className={s.col}>
              <b className={s.clamp} title={n.name}>{n.name}</b>
              <span className={s.tags}><DocTags docs={n.docs} /><NatTag nat={n.nat} /></span>
            </span>
            {sup && <span style={{ color: n.rcpt ? 'var(--tx)' : 'var(--err)' }}>{n.rcpt ? NORM_RCPT[n.rcpt] : tr('nrm.unset')}</span>}
            <span className={s.tx2}>{NORM_PORT[n.port]}</span>
            <span className={s.tx2}>{NORM_UNIT[n.unit]}</span>
            <b className={s.rb}>{vnd(x.amt)}</b>
            <span className={`${s.r} ${s.tx2} ${s.nw}`}>{thrText(x.thrP, x.thrA)}</span>
            <span className={s.apr} title={NORM_APR[n.apr][2]}>
              <span>{NORM_APR[n.apr][0]}</span>
              {NORM_APR[n.apr][1] && <span className={s.ktt}>{NORM_APR[n.apr][1]}</span>}
            </span>
            <span className={s.col}>
              <span className={s.flex}>
                <span className={s.st} style={pill(x.st)}>{NORM_ST[x.st][0]}</span>
                {nx && (
                  <span role="img" className={s.nx} title={tr('nrm.nextTip', { v: nx.v, st: NORM_ST[nx.st][0], from: dk(nx.from), amt: vnd(nx.amt) })} aria-label={tr('nrm.nextTip', { v: nx.v, st: NORM_ST[nx.st][0], from: dk(nx.from), amt: vnd(nx.amt) })} style={{ color: NORM_ST[nx.st][1] }}>
                    <IconCalendarEvent size={15} stroke={1.8} aria-hidden="true" />
                  </span>
                )}
              </span>
              <span className={`${s.lb} ${s.nw}`}>{x.v} · {dk(x.from)} → {dk(x.to)}</span>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Detail({ n, vSel, setV, ask, setAsk, noWrite }: {
  n: NormView; vSel: number | null; setV: (i: number | null) => void; ask: boolean; setAsk: (b: boolean) => void; noWrite: () => void;
}) {
  const tr = useT();
  const logQ = useList('normLog', { limit: 200 });
  const view: NormVerView = vSel !== null ? n.vers[vSel] ?? n.cur : n.cur;
  const old = view.i !== n.cur.i;
  const S = normStats(n);
  const sug = Math.ceil(S.p90 / 10000) * 10000;
  const mxW = Math.max(S.lim, ...S.wavg) * 1.06;
  const flo = Math.min(n.cur.amt, ...S.wavg.filter((a) => a > 0)) * 0.7;
  const yp = (a: number) => Math.round(((a - flo) / Math.max(1, mxW - flo)) * 100);
  const sv = svOf(n.sv);
  const kpi: Array<[string, string, string]> = [
    [tr('nrm.k.norm'), `${vnd(view.amt)} ₫`, `/ ${NORM_UNIT[n.unit]}`],
    [tr('nrm.thr'), thrText(view.thrP, view.thrA), ''],
    [tr('nrm.apr'), `${NORM_APR[n.apr][0]}${NORM_APR[n.apr][1] ? ` ${NORM_APR[n.apr][1]}` : ''}`, ''],
    [tr('nrm.eff'), `${dk(view.from)} → ${dk(view.to)}`, NORM_ST[view.st][0]],
  ];
  const log = logOf(logQ.data?.items ?? [], n);
  const sw = (a: number) => (a > S.lim ? 'var(--d-amber)' : 'var(--d-blue)');
  return (
    <>
      <div className={s.dMeta}>
        <Code n={n} w={52} h={34} />
        <span className={s.dSub}>
          <span className={s.flex} title={tr('nrm.port')}><IconMapPin size={13} stroke={1.8} aria-hidden="true" />{NORM_PORT[n.port]}</span>
          <span className={s.flex} title={tr('nrm.unit')}><IconContainer size={13} stroke={1.8} aria-hidden="true" />{NORM_UNIT[n.unit]}</span>
        </span>
      </div>
      <div className={s.dActs}>
        <Button variant="primary" icon={<IconEdit size={16} aria-hidden="true" />} onClick={noWrite}>{tr('nrm.edit')}</Button>
        <button type="button" className={s.ask} aria-label={tr('nrm.askTitle')} title={tr('nrm.askTitle')} onClick={() => setAsk(true)}>
          <IconSparkles size={16} stroke={1.9} aria-hidden="true" />{tr('nrm.suggest')}
        </button>
        <span className={s.right}>
          <HeaderTool label={tr('nrm.clone')} icon={IconCopy} onClick={noWrite} />
          <HeaderTool label={tr('nrm.stop')} icon={IconPlayerPause} onClick={noWrite} />
        </span>
      </div>
      {old && (
        <div className={s.oldBar} role="status">
          <IconHistory size={16} stroke={1.8} aria-hidden="true" />
          <span className={s.fw}>{tr('nrm.viewing', { v: view.v, st: NORM_ST[view.st][0] })}</span>
          <button type="button" className={s.back} onClick={() => setV(null)}><IconArrowBackUp size={14} stroke={1.8} aria-hidden="true" />{tr('nrm.current')}</button>
        </div>
      )}
      <div className={s.kpi}>
        {kpi.map(([k, v, sub]) => (
          <div key={k} className={s.kpiC}><div className={s.lb}>{k}</div><div className={s.kpiV}>{v}</div>{sub && <div className={s.lb}>{sub}</div>}</div>
        ))}
      </div>
      <div className={s.info}>
        <div className={s.irow}><span className={s.lb}>{tr('nrm.service')}</span><span>{sv[0]} · {sv[1]}</span></div>
        <div className={s.irow}><span className={`${s.lb} ${s.flex}`}><IconReceipt size={13} stroke={1.8} aria-hidden="true" />{tr('nrm.docs')}</span><span className={s.tags}><DocTags docs={n.docs} /></span></div>
        <div className={s.irow}><span className={s.lb}>{tr('nrm.nature')}</span><span className={s.tags}><NatTag nat={n.nat} /></span></div>
        {n.grp === 'sup' && (
          <>
            <div className={s.irow}><span className={s.lb}>{tr('nrm.group')}</span><span>{tr('nrm.supFee', { sv: sv[3] })}</span></div>
            <div className={s.irow}><span className={s.lb}>{tr('nrm.c.rcpt')}</span><b className={s.fw}>{n.rcpt ? NORM_RCPT[n.rcpt] : '—'}</b></div>
          </>
        )}
      </div>
      {ask && (
        <section className={s.askBox} aria-label={tr('nrm.askTitle')}>
          <div className={`${s.flex} ${s.askHead}`}>
            <IconSparkles size={16} stroke={1.9} aria-hidden="true" /><b>Ask HANA</b>
            <HeaderTool label={tr('nrm.closeSug')} icon={IconX} onClick={() => setAsk(false)} />
          </div>
          <div className={s.askRow}>
            <b className={s.askV}>{vnd(sug)} ₫</b>
            <span className={s.askD} title={tr('nrm.vsCur')}>{sug > n.cur.amt ? '+' : ''}{kk(sug - n.cur.amt)}</span>
            <span className={s.tx2} title={tr('nrm.sugThr')}>{thrText(n.cur.thrP || 5, n.cur.thrA)}</span>
            <span className={s.lb} title={tr('nrm.basis')}>P90 {kk(S.p90)} · {tr('nrm.over', { a: S.over, b: S.N })}</span>
          </div>
          <div><Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} onClick={noWrite}>{tr('nrm.newVer')}</Button></div>
        </section>
      )}
      <section className={s.sec}>
        <div className={s.secH}>
          <IconChartBar size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('nrm.actual')}</h3>
          <span className={s.leg}>
            <span className={s.flex}><span className={s.lnS} />{tr('nrm.k.norm')} {kk(n.cur.amt)}</span>
            <span className={s.flex}><span className={s.lnD} />{tr('nrm.limit')} {kk(S.lim)}</span>
          </span>
        </div>
        <div className={s.stats}>
          <div><div className={s.lb}>{tr('nrm.count')}</div><div className={s.sv}>{S.N}</div></div>
          <div><div className={s.lb}>{tr('nrm.avg')}</div><div className={s.sv} style={{ color: S.avg > n.cur.amt ? 'var(--warn)' : 'var(--tx)' }}>{vnd(Math.round(S.avg / 1000) * 1000)}</div></div>
          <div><div className={s.lb}>P90</div><div className={s.sv} style={{ color: S.p90 > S.lim ? 'var(--warn)' : 'var(--tx)' }}>{vnd(S.p90)}</div></div>
          <div><div className={s.lb}>{tr('nrm.overN')}</div><div className={s.sv} style={{ color: S.over ? 'var(--warn)' : 'var(--ok)' }}>{S.over}/{S.N}</div></div>
        </div>
        <div className={s.chart} role="img" aria-label={tr('nrm.chart')}>
          <div className={s.bars}>
            {S.wavg.map((a, i) => <span key={i} title={tr('nrm.week', { n: i + 1, avg: a ? vnd(Math.round(a / 1000) * 1000) : '—' })} style={{ height: `${a ? Math.max(4, yp(a)) : 0}%`, background: sw(a) }} />)}
          </div>
          <div aria-hidden="true" className={s.lineS} style={{ top: `${100 - yp(n.cur.amt)}%` }} />
          <div aria-hidden="true" className={s.lineD} style={{ top: `${100 - yp(Math.min(S.lim, mxW))}%` }} />
        </div>
        <div className={`${s.axis} ${s.lb}`}><span>23/Jul</span><span>22/Oct</span></div>
      </section>
      <section className={s.sec}>
        <div className={s.secH}><IconArchive size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('nrm.versions')}</h3></div>
        <div className={s.vers}>
          {n.vers.slice().reverse().map((v) => (
            <button key={v.v} type="button" className={s.ver} style={{ background: v.i === view.i ? 'var(--pri-bg)' : undefined }} aria-current={v.i === view.i} aria-label={tr('nrm.seeV', { v: v.v })} onClick={() => setV(v.i === n.cur.i ? null : v.i)}>
              <b className={s.tx2}>{v.v}</b>
              <span className={s.vMain}>
                <b>{vnd(v.amt)}</b><span className={s.tx2}>{thrText(v.thrP, v.thrA)}</span>
                <span className={`${s.flex} ${s.lb}`}><IconCalendarEvent size={13} stroke={1.8} aria-hidden="true" />{dk(v.from)} → {dk(v.to)}</span>
              </span>
              <span className={s.st} style={pill(v.st)}>{NORM_ST[v.st][0]}</span>
            </button>
          ))}
        </div>
      </section>
      <section className={s.sec}>
        <div className={s.secH}><IconHistory size={16} stroke={1.8} aria-hidden="true" /><h3>{tr('nrm.history')}</h3></div>
        <div>
          {log.map((l, i) => (
            <div key={`${l.at}-${i}`} className={s.log} style={{ borderTop: i ? '1px solid var(--line)' : 0 }}>
              <span className={s.av} title={l.who} aria-hidden="true">{l.who.slice(0, 2)}</span>
              <div className={s.logB}>
                <div className={s.flex}><b className={s.fw}>{l.who}</b><span className={s.lb}>{dkt(l.at)}</span></div>
                <div className={s.lb}>{l.what}</div>
                <div className={s.flex}><span className={s.lb} style={{ textDecoration: 'line-through' }}>{l.old}</span><span className={s.lb} aria-label={tr('nrm.to')}>→</span><b>{l.now}</b></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function NormsTable() {
  const tr = useT();
  const toast = useToast();
  const acc = useFinanceAccess();
  const rowsQ = useList('normRow', { limit: 200 });
  const [sv, setSv] = useState('CCF');
  const [grpF, setGrpF] = useState<'all' | NormRow['grp']>('all');
  const [pf, setPf] = useState('all');
  const [all, setAll] = useState(false);
  const [sel, setSel] = useState<{ id: string | null; v: number | null }>(() => ({ id: initSel(), v: null }));
  const [ask, setAsk] = useState(false);

  let state: ScreenState = financeScreenState(acc);
  if (state !== 'noperm' && !acc.force) {
    if (rowsQ.isLoading) state = 'loading';
    else if (rowsQ.isError) state = rowsQ.error instanceof DataError && rowsQ.error.code === 'noperm' ? 'noperm' : 'error';
    else if ((rowsQ.data?.items.length ?? 0) === 0) state = 'empty';
  }
  const noperm = state === 'noperm';
  const ready = state === 'ready';
  const all0 = resolveNorms(ready ? rowsQ.data?.items ?? [] : []);
  const noWrite = () => toast({ msg: tr('nrm.noWrite') });
  const soonTo = (what: string) => () => toast({ msg: tr('nrm.soon', { what }) });

  const waitAll = all0.filter((n) => n.vers.some((v) => v.st === 'wait')).length;
  const schedAll = all0.filter((n) => n.vers.some((v) => v.st === 'sched')).length;
  const std = normRows(all0, sv, pf, 'std', all);
  const sup = normRows(all0, sv, pf, 'sup', all);
  const nStd = countOf(all0, sv, pf, 'std');
  const nSup = countOf(all0, sv, pf, 'sup');
  const svT = svOf(sv);
  const showStd = grpF !== 'sup' && std.length > 0;
  const showSup = grpF !== 'std' && sup.length > 0;
  const selN = all0.find((n) => n.id === sel.id) ?? null;
  const pick = (id: string, v: number | null) => { setSel({ id, v }); setAsk(false); };
  const close = () => { setSel({ id: null, v: null }); setAsk(false); };

  return (
    <Page kind="table">
      <PageHeader
        title={tr('nrm.title')}
        meta={ready ? (
          <>
            {waitAll > 0 && <span className={`${s.mchip} ${s.mWait}`} title={tr('nrm.waitT')} aria-label={tr('nrm.waitN', { n: waitAll })}><IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />{waitAll}</span>}
            {schedAll > 0 && <span className={`${s.mchip} ${s.mSched}`} title={tr('nrm.schedT')} aria-label={tr('nrm.schedN', { n: schedAll })}><IconCalendarEvent size={13} stroke={1.8} aria-hidden="true" />{schedAll}</span>}
          </>
        ) : undefined}
        actions={
          <>
            <label className={s.port} title={tr('nrm.port')}>
              <IconMapPin size={15} stroke={1.8} aria-hidden="true" />
              <select aria-label={tr('nrm.port')} value={pf} onChange={(e) => setPf(e.target.value)}>
                <option value="all">{tr('nrm.allPorts')}</option>
                {Object.keys(NORM_PORT).filter((k) => k !== '*').map((k) => <option key={k} value={k}>{NORM_PORT[k]}</option>)}
              </select>
            </label>
            <HeaderTool label={tr('nrm.imp')} icon={IconUpload} onClick={noWrite} />
            <HeaderTool label={tr('nrm.exp')} icon={IconDownload} onClick={soonTo(tr('nrm.exp'))} />
            <HeaderTool label={tr('nrm.history')} icon={IconHistory} onClick={soonTo(tr('nrm.history'))} />
            <Button variant="primary" icon={<IconPlus size={16} aria-hidden="true" />} disabled={noperm} onClick={noWrite}>{tr('nrm.add')}</Button>
          </>
        }
      />
      <StateView
        state={noperm ? 'ready' : state}
        title={state === 'empty' ? tr('nrm.empty') : undefined}
        desc={state === 'empty' ? tr('nrm.emptyDesc') : undefined}
        icon={state === 'empty' ? <IconFileText size={26} stroke={1.8} /> : undefined}
        action={state === 'error' ? <Button onClick={() => void rowsQ.refetch()}>{tr('nrm.retry')}</Button> : undefined}
        skeleton={<Skeleton rows={9} />}
      >
        {noperm ? (
          <section className={s.none} role="status">
            <IconLock size={28} stroke={1.8} aria-hidden="true" />
            <b>{tr('nrm.noperm')}</b>
          </section>
        ) : (
          <div className={s.body}>
            <nav className={s.tree} aria-label={tr('nrm.service')}>
              {NORM_SV.map(([k, name, color]) => {
                const on = k === sv;
                const w = waitOf(all0, k);
                const kids: Array<['all' | NormRow['grp'], string, number]> = [['all', tr('nrm.all'), countOf(all0, k, pf)], ['std', tr('nrm.std'), countOf(all0, k, pf, 'std')]];
                const ns = countOf(all0, k, pf, 'sup');
                if (ns) kids.push(['sup', tr('nrm.supFee', { sv: svOf(k)[3] }), ns]);
                return (
                  <div key={k}>
                    <button type="button" className={`${s.node} ${on ? s.nodeOn : ''}`} aria-current={on} onClick={() => { setSv(k); setGrpF('all'); close(); }}>
                      <span className={s.nIc} style={tile(color)}>{k}</span>
                      <span className={s.ell}>{name}</span>
                      {w > 0 && <span className={s.wait} title={tr('nrm.waitT')} aria-label={tr('nrm.waitS', { n: w })}><IconClockHour4 size={13} stroke={1.8} aria-hidden="true" />{w}</span>}
                      <span className={s.lb}>{countOf(all0, k, pf)}</span>
                    </button>
                    {on && kids.map(([g, l, c]) => (
                      <button key={g} type="button" className={`${s.kid} ${grpF === g ? s.kidOn : ''}`} aria-current={grpF === g} onClick={() => setGrpF(g)}>
                        <span className={s.ell}>{l}</span><span className={s.lb}>{c}</span>
                      </button>
                    ))}
                  </div>
                );
              })}
            </nav>
            <section className={s.pane} aria-label={tr('nrm.paneLabel', { name: svT[1] })}>
              <div className={s.pbar}>
                <span className={s.pTile} style={tile(svT[2])}>{svT[0]}</span>
                <h2>{svT[1]}</h2>
                <span className={s.lb} title={tr('nrm.countT')}>{nStd + nSup}</span>
                <span className={s.right}>
                  <Segmented label={tr('nrm.versions')} value={all ? 'all' : 'cur'} onChange={(k) => setAll(k === 'all')} items={[{ key: 'cur', label: tr('nrm.eff') }, { key: 'all', label: tr('nrm.allVers') }]} />
                </span>
              </div>
              <div className={s.scroll}>
                {showStd && <Group rows={std} sup={false} title={tr('nrm.std')} count={nStd} pick={pick} sel={sel} />}
                {showSup && <Group rows={sup} sup title={tr('nrm.supFee', { sv: svT[3] })} count={nSup} pick={pick} sel={sel} />}
                {!showStd && !showSup && <div className={s.empty}>{tr('nrm.none')}</div>}
              </div>
            </section>
          </div>
        )}
      </StateView>
      <Drawer open={!!selN} onOpenChange={(o) => !o && close()} title={selN?.name ?? tr('nrm.k.norm')}> 
        {selN && <Detail n={selN} vSel={sel.v} setV={(i) => setSel({ id: selN.id, v: i })} ask={ask} setAsk={setAsk} noWrite={noWrite} />}
      </Drawer>
    </Page>
  );
}

export function Norms() {
  return useLayout() === 'pad' ? <NormsCompact /> : <NormsTable />;
}
